/**
 * Situação do período de teste no card "Plano Atual" (chamado 7176): até
 * quando vai o teste, para qual plano o assinante vai depois e o caminho para
 * assinar antes do fim — sem bloquear nada.
 *
 * Com a assinatura já pedida e a cobrança em aberto, o card oferece pagar ou
 * continuar no teste. O membro convidado só vê a situação: quem assina é o
 * titular.
 */
import { CreditCard, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  TRIAL_MEMBER_HINT,
  TRIAL_PENDING_COPY,
  canSubscribeInTrial,
  isTrialConversionPending,
  trialAfterEndText,
  trialEndLabel,
  trialExitLabel,
} from '@/lib/trial-subscription';
import type { UserSubscription } from '@/services/account.service';

interface TrialPlanStatusProps {
  subscription: UserSubscription;
  canManage: boolean;
  keepingTrial: boolean;
  onSubscribe: () => void;
  onPay: () => void;
  onKeepTrial: () => void;
}

export function TrialPlanStatus({
  subscription,
  canManage,
  keepingTrial,
  onSubscribe,
  onPay,
  onKeepTrial,
}: Readonly<TrialPlanStatusProps>) {
  const trial = subscription.trial;
  if (!trial) return null;

  const renderAcao = () => {
    if (!canManage) {
      return <p className="text-sm text-muted-foreground">{TRIAL_MEMBER_HINT}</p>;
    }
    if (isTrialConversionPending(subscription)) {
      return (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
          <p className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200">
            <CreditCard className="h-4 w-4 flex-shrink-0" />
            {TRIAL_PENDING_COPY.title}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={onPay} disabled={keepingTrial}>
              Pagar agora
            </Button>
            <Button variant="outline" onClick={onKeepTrial} disabled={keepingTrial}>
              {keepingTrial && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {trialExitLabel(subscription)}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <Button onClick={onSubscribe} disabled={!canSubscribeInTrial(subscription)}>
        Assinar agora
      </Button>
    );
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="space-y-1 text-sm">
        <p className="font-medium text-foreground">{trialEndLabel(trial)}</p>
        <p className="text-muted-foreground">{trialAfterEndText(trial)}</p>
      </div>
      {renderAcao()}
    </div>
  );
}

export default TrialPlanStatus;
